// Fill with AI (Dean, 14 Oct 2026): GPT-5.5 reads the file (notes · calendar · emails · file names · PDF text · Airtable row) and proposes map
// recordings per party. Before Apply the dialog shows the plan — every ticked line in apply order, what it depends on, what blocks it — so
// nothing fails silently; a missing date is fixed inline, a sub-timeline the arrow does not offer is recorded as a one-off, and failed
// lines keep their fix controls with a Retry. Each applied line is a normal, undoable recording.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Loader2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { applyLegalMapAiFill, getLegalMapAiRun, planLegalMapAiFill, startLegalMapAiFill } from "@/lib/api";
import { PRIMARY_BTN, fmtDY } from "./mapShared";
import { ResultsList, Sources, TrackGroup } from "./MapAiLines";

const errText = (e) => { const d = e?.response?.data?.detail; return typeof d === "string" ? d : d?.message || e.message; };

export function MapAiFillDialog({ caseRow, onClose, onDone }) {
  const [run, setRun] = useState(null);
  const [picks, setPicks] = useState(new Set());
  const [edits, setEdits] = useState({});
  const [plan, setPlan] = useState({});       // proposal id → plan line
  const [planMeta, setPlanMeta] = useState(null);
  const [diary, setDiary] = useState(false);
  const [busy, setBusy] = useState(false);
  const [rounds, setRounds] = useState([]);   // apply results, one list per round
  const timer = useRef(null);
  const planReq = useRef(0);
  const started = useRef(false);   // StrictMode re-runs the effect — one GPT run per open, not two
  const mounted = useRef(false);

  useEffect(() => {
    mounted.current = true;
    if (!started.current) {
      started.current = true;
      (async () => {
        try {
          const r = await startLegalMapAiFill(caseRow.case_id);
          if (!mounted.current) return;
          setRun(r);
          const poll = async () => {
            try {
              const cur = await getLegalMapAiRun(caseRow.case_id, r.run_id);
              if (!mounted.current) return;
              setRun(cur);
              if (cur.status === "done") { setPicks(new Set((cur.proposals || []).filter((p) => !p.problem && p.confidence !== "low").map((p) => p.id))); return; }
              if (cur.status === "failed") return;
            } catch { /* keep polling */ }
            timer.current = setTimeout(poll, 2500);
          };
          timer.current = setTimeout(poll, 2500);
        } catch (e) { toast.error(errText(e)); onClose(); }
      })();
    }
    return () => { mounted.current = false; if (timer.current) clearTimeout(timer.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseRow.case_id]);

  // the plan follows every tick and date fix (debounced)
  useEffect(() => {
    if (run?.status !== "done" || rounds.length) return undefined;
    const id = ++planReq.current;
    const t = setTimeout(async () => {
      try {
        const r = await planLegalMapAiFill(caseRow.case_id, run.run_id, { picks: [...picks], edits });
        if (planReq.current !== id) return;
        setPlan(Object.fromEntries((r.plan || []).map((l) => [l.id, l])));
        setPlanMeta({ ready: r.ready, blocked: r.blocked, one_offs: r.one_offs });
      } catch { /* the plan is advisory */ }
    }, 300);
    return () => clearTimeout(t);
  }, [caseRow.case_id, run?.run_id, run?.status, picks, edits, rounds.length]);

  const proposals = useMemo(() => run?.proposals || [], [run?.proposals]);
  const usable = proposals.filter((p) => !p.problem);
  const toggle = useCallback((id, on) => setPicks((s) => { const n = new Set(s); on ? n.add(id) : n.delete(id); return n; }), []);
  const setEdit = useCallback((id, patch) => setEdits((e) => ({ ...e, [id]: { ...(e[id] || {}), ...patch } })), []);
  // party → track → lines (original AI order = chronological; add-party lines sit first)
  const tree = useMemo(() => {
    const out = [];
    for (const p of proposals) {
      let party = out.find((x) => x.party === p.party_name);
      if (!party) { party = { party: p.party_name, party_id: p.party_id, tracks: [] }; out.push(party); }
      const tname = p.action === "add_party" ? "Party" : p.timeline_name || p.track;
      let tr = party.tracks.find((x) => x.name === tname);
      if (!tr) { tr = { name: tname, lines: [] }; party.tracks.push(tr); }
      tr.lines.push(p);
    }
    return out;
  }, [proposals]);

  const doApply = async (ids) => {
    setBusy(true);
    try {
      const r = await applyLegalMapAiFill(caseRow.case_id, run.run_id, { picks: ids, edits, diary });
      setRounds((rs) => [...rs, r.results]);
      toast[r.failed ? "warning" : "success"](`${r.applied} recording${r.applied === 1 ? "" : "s"} applied${r.one_offs ? ` · ${r.one_offs} as one-off${r.one_offs === 1 ? "" : "s"}` : ""}${r.forced ? ` · ${r.forced} forced past an open sub-timeline` : ""}${r.skipped ? ` · ${r.skipped} already there` : ""}${r.failed ? ` · ${r.failed} not applied — fix and retry` : ""}`);
      onDone?.(r);
    } catch (e) { toast.error(errText(e)); }
    finally { setBusy(false); }
  };
  const retry = () => { const last = rounds[rounds.length - 1] || []; doApply(last.filter((r) => !r.ok).map((r) => r.id)); };
  const ready = planMeta?.ready ?? picks.size;

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="map-ai-dialog">
        <DialogHeader>
          <DialogTitle className="text-sm flex items-center gap-2"><Sparkles className="w-4 h-4 text-[#E5243B]" /> Fill with AI — {caseRow.customer_name}</DialogTitle>
          <DialogDescription className="text-xs">GPT-5.5 reads the notes, timeline, Legal calendar, logged emails, every file name, the newest PDFs and the Airtable Legal Tracker row, then proposes what to record. Tick the lines, fix any date inline, and apply — the plan below shows what each line will do before anything is written; every line is undoable.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 text-xs">
          {(!run || run.status === "running") && (
            <div className="flex items-center gap-2 text-slate-700 py-6 justify-center" data-testid="map-ai-running"><Loader2 className="w-4 h-4 animate-spin" /> {run?.stage || "starting…"}</div>
          )}
          {run?.status === "failed" && <div className="rounded border border-rose-300 bg-rose-50 px-2.5 py-2 text-rose-900" data-testid="map-ai-failed">The run failed: {run.error}</div>}
          {run?.status === "done" && !rounds.length && (
            <>
              <div className="rounded border border-slate-200 bg-slate-50 px-2.5 py-2 space-y-1">
                <div className="text-slate-800" data-testid="map-ai-summary">{run.summary || "Nothing to propose."}</div>
                <Sources s={run.sources} />
              </div>
              {!proposals.length && <div className="text-slate-500 italic text-center py-4" data-testid="map-ai-empty">No recordings proposed — the map already shows what the file shows, or the evidence was not clear enough.</div>}
              {tree.map((pt) => (
                <div key={pt.party} className="space-y-1.5" data-testid={`map-ai-party-${pt.party_id}`}>
                  <div className="text-[9px] uppercase tracking-wider text-slate-500 font-semibold">{pt.party}</div>
                  {pt.tracks.map((tr) => <TrackGroup key={tr.name} name={tr.name} lines={tr.lines} picks={picks} toggle={toggle} plan={plan} edits={edits} setEdit={setEdit} />)}
                </div>
              ))}
              {run.seen?.length > 0 && (
                <div className="text-[10px] text-slate-500" data-testid="map-ai-seen"><span className="font-medium">Seen but not proposed:</span> {run.seen.join(" · ")}</div>
              )}
              {proposals.length > 0 && (
                <div className="flex items-center gap-3 flex-wrap pt-1 border-t">
                  <label className="flex items-center gap-1.5"><Checkbox checked={picks.size === usable.length && usable.length > 0} onCheckedChange={(v) => setPicks(v ? new Set(usable.map((p) => p.id)) : new Set())} data-testid="map-ai-tick-all" /> tick all usable</label>
                  <label className="flex items-center gap-1.5" title="Off = history only: no Legal calendar actions are created for back-dated steps"><Checkbox checked={diary} onCheckedChange={(v) => setDiary(!!v)} data-testid="map-ai-diary" /> also create the steps' Legal calendar actions</label>
                  {planMeta && picks.size > 0 && (
                    <span className="ml-auto text-[10px] text-slate-600" data-testid="map-ai-plan-summary">
                      <span className="font-semibold text-emerald-800">{planMeta.ready} ready</span>
                      {planMeta.one_offs > 0 && <> · <span className="text-violet-800">{planMeta.one_offs} as one-off{planMeta.one_offs === 1 ? "" : "s"}</span></>}
                      {planMeta.blocked > 0 && <> · <span className="text-rose-800">{planMeta.blocked} blocked — fix or untick</span></>}
                    </span>
                  )}
                </div>
              )}
            </>
          )}
          {rounds.length > 0 && <ResultsList rounds={rounds} edits={edits} setEdit={setEdit} onRetry={retry} busy={busy} />}
        </div>
        <DialogFooter>
          {run?.finished_at && <span className="mr-auto text-[10px] text-slate-400">{run.model} · {fmtDY(run.finished_at)}</span>}
          <Button variant="outline" size="sm" onClick={onClose} data-testid="map-ai-close">{rounds.length ? "Close" : "Cancel"}</Button>
          {run?.status === "done" && !rounds.length && proposals.length > 0 && (
            <Button size="sm" className={PRIMARY_BTN} disabled={busy || !picks.size || ready === 0} onClick={() => doApply([...picks])} data-testid="map-ai-apply" title={ready === 0 && picks.size ? "Every ticked line is blocked — fix the dates or tick the lines they depend on" : undefined}>
              {busy && <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />}Apply {ready} recording{ready === 1 ? "" : "s"}{planMeta?.blocked ? ` (${planMeta.blocked} blocked left for retry)` : ""}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
